import type { ResearchResult } from '../../core/intelligence/lead-research.js'
import { getVisibleStageForStage, type FunnelStage } from '../../core/types/funnel-stage.js'
import type { TranscriptItem } from '../../types/visual'
import type {
  ConciergeFormData,
  ConciergeSourceRow,
  FutureMessage,
  FutureMultimodalPresence,
  FutureSessionState,
  FutureSource,
  IntelligenceBriefing,
} from './future-chat-types'
import { DEFAULT_UI_LOCALE, type UiLocale } from '../../core/contracts.js'
import { clientSessionViewStore } from '../../../services/clientSessionViewStore.js'
import { normalizeDiscoveryReportTranscriptItem } from './future-chat-report-normalize.js'
import { analyzeConsultantState } from '../../core/agents/utils/consultant-state.js'
import { resolveVisitorFacingCompanyName } from '../../lib/company-search-label.js'
import { lastMeaningfulTurnIsUnansweredVisitor } from '../../lib/thinking-latch.js'
import { isGenericLeadRole } from '../../lib/identity-briefing-merge.js'
import { readGroundedPack } from '../../core/intelligence/grounded-pack.js'

export const FUTURE_ASSISTANT_ORIGIN_LABEL = 'F.B/c'
export const FUTURE_USER_ORIGIN_LABEL = 'You'

function hasConcreteRecommendation(messages: FutureMessage[]): boolean {
  return messages.some(
    message =>
      message.role === 'assistant' &&
      message.attachment?.type !== 'intelligence_briefing' &&
      /(recommend|recommendation|start with|start by|first action|first step|on monday|begin with|set up|create a|map the|automate|use a)/i.test(
        message.body
      )
  )
}

export function resolveFutureSessionStage(params: {
  messages: FutureMessage[]
  currentAgentStage?: FunnelStage | undefined
  isResearching?: boolean | undefined
}): FunnelStage {
  const baseStage = params.currentAgentStage || 'DISCOVERY'
  if (params.messages.some(message => message.attachment?.type === 'discovery_report')) {
    return 'SUMMARY'
  }

  const consultantState = analyzeConsultantState({
    messages: params.messages.map(message => ({
      id: message.id,
      role: message.role,
      content: message.body,
      timestamp: new Date(message.timestamp),
    })),
  })
  const signalStage = hasConcreteRecommendation(params.messages)
    ? 'PITCHING'
    : params.isResearching || consultantState.corePainCaptured
      ? 'QUALIFIED'
      : baseStage
  const baseOrder = getVisibleStageForStage(baseStage).order
  const signalOrder = getVisibleStageForStage(signalStage).order
  return signalOrder >= baseOrder ? signalStage : baseStage
}

function activeReportLocale(): UiLocale {
  return clientSessionViewStore.getSnapshot().uiLocale ?? DEFAULT_UI_LOCALE
}

export function mapTranscriptToFutureMessages(
  items: TranscriptItem[],
  locale: UiLocale = activeReportLocale(),
): FutureMessage[] {
  return items.map(item => {
    const normalizedItem = normalizeDiscoveryReportTranscriptItem(item, locale)
    const isAssistant = normalizedItem.role === 'model'
    const images =
      normalizedItem.attachment?.type === 'image'
        ? ([
            normalizedItem.attachment.url ||
              (normalizedItem.attachment.data
                ? `data:${normalizedItem.attachment.mimeType || 'image/jpeg'};base64,${normalizedItem.attachment.data}`
                : undefined),
          ].filter(Boolean) as string[])
        : undefined
    const sources = extractSourcesFromItem(normalizedItem)
    const webSearchQueries = (normalizedItem.groundingMetadata?.webSearchQueries || []).filter(
      (query): query is string => typeof query === 'string' && query.trim().length > 0
    )
    return {
      id: normalizedItem.id,
      role: isAssistant ? 'assistant' : 'user',
      label: isAssistant ? FUTURE_ASSISTANT_ORIGIN_LABEL : FUTURE_USER_ORIGIN_LABEL,
      timestamp: formatTimestamp(normalizedItem.timestamp),
      body: normalizedItem.text,
      ...(sources.length > 0 ? { sources, sourceIds: sources.map(source => source.id) } : {}),
      ...(webSearchQueries.length > 0 ? { webSearchQueries } : {}),
      ...(normalizedItem.attachment ? { attachment: normalizedItem.attachment } : {}),
      isFinal: normalizedItem.isFinal,
      ...(normalizedItem.status ? { status: normalizedItem.status } : {}),
      ...(normalizedItem.processingTime ? { processingTime: normalizedItem.processingTime } : {}),
      reasoning: normalizedItem.reasoning ? [normalizedItem.reasoning] : undefined,
      error:
        normalizedItem.error?.message ||
        (normalizedItem.status === 'error' ? 'An error occurred' : undefined),
      images: images,
    }
  })
}

export function derivePresenceFromContext(
  snapshot: {
    multimodalContext?: { hasActiveCamera?: boolean; hasActiveScreenShare?: boolean }
    sessionPermissions?: {
      voice?: boolean
      webcam?: boolean
      location?: boolean
      research?: boolean
    }
  },
  runtime?: {
    voiceActive?: boolean | undefined
    visionActive?: boolean | undefined
    screenShared?: boolean | undefined
    locationShared?: boolean | undefined
    memoryActive?: boolean | undefined
  }
): FutureMultimodalPresence {
  return {
    voiceActive: runtime?.voiceActive ?? !!snapshot.sessionPermissions?.voice,
    visionActive:
      runtime?.visionActive ??
      (!!snapshot.multimodalContext?.hasActiveCamera || !!snapshot.sessionPermissions?.webcam),
    screenShared: runtime?.screenShared ?? !!snapshot.multimodalContext?.hasActiveScreenShare,
    memoryActive: runtime?.memoryActive ?? !!snapshot.sessionPermissions?.research,
    locationShared: runtime?.locationShared ?? !!snapshot.sessionPermissions?.location,
  }
}

export function buildFutureSessionFromTranscript(
  items: TranscriptItem[],
  context?: unknown
): FutureSessionState {
  const sources = dedupeSources(items.flatMap(extractSourcesFromItem))
  const briefing = extractFutureBriefingFromTranscript(items)
  const evidenceState = buildSessionEvidenceState({
    briefing,
    transcript: items,
    researchRan: !!briefing && briefing.sources.length > 0,
  })
  const typedContext = context as
    | { currentAgentStage?: string; presence?: FutureMultimodalPresence }
    | undefined
  const stageLabel = typedContext?.currentAgentStage
    ? formatStageLabel(typedContext.currentAgentStage)
    : 'Discovery'

  // Confidence: use evidenceState for source-backed briefings, 0 for intake-only
  const displayConfidence =
    evidenceState.confidenceTier !== 'not_source_backed' &&
    evidenceState.confidenceScore !== undefined
      ? evidenceState.confidenceScore
      : 0

  return {
    mode: 'discovery',
    stateLabel: 'Active',
    modeLabel: stageLabel,
    focusLabel: items.length > 0 ? 'Conversation' : 'New session',
    confidence: displayConfidence,
    sources,
    context: {
      label: 'Session context',
      memoryCountLabel: `${items.length} turns`,
      activeLayers: [...(sources.length > 0 ? ['sources'] : []), ...(briefing ? ['briefing'] : [])],
    },
    presence: typedContext?.presence || {
      voiceActive: false,
      visionActive: false,
      screenShared: false,
      locationShared: false,
      memoryActive: sources.length > 0 || !!briefing,
    },
  }
}

/** Briefing card is confirmable once public sources exist or research explicitly failed. */
export function isSourceBackedBriefing(briefing: IntelligenceBriefing | null | undefined): boolean {
  return (briefing?.sources?.length ?? 0) > 0
}

export function shouldUseIntakeFallbackBriefing(input: {
  preflightFailed: boolean
  preflightTimedOut: boolean
}): boolean {
  return input.preflightFailed || input.preflightTimedOut
}

export function isBriefingReadyToConfirm(
  briefing: IntelligenceBriefing | null | undefined,
  input: { preflightFailed: boolean; preflightTimedOut: boolean }
): boolean {
  if (!briefing) return false
  return isSourceBackedBriefing(briefing) || shouldUseIntakeFallbackBriefing(input)
}

export function hasAssistantWelcomeMessage(messages: FutureMessage[]): boolean {
  return messages.some(
    m =>
      m.role === 'assistant' &&
      m.isFinal !== false &&
      Boolean(m.body?.trim()) &&
      m.body !== '...' &&
      m.attachment?.type !== 'intelligence_briefing'
  )
}

/** Once a welcome has been seen, keep composer/voice unlocked even if that row is later replaced. */
export function latchAssistantWelcome(previous: boolean, messages: FutureMessage[]): boolean {
  return previous || hasAssistantWelcomeMessage(messages)
}

/** Last visitor turn is still waiting — empty trailing assistant shells do not count. */
export function hasPendingAssistantReply(messages: FutureMessage[]): boolean {
  if (messages.length === 0) return false
  return lastMeaningfulTurnIsUnansweredVisitor(
    messages.map(message => ({
      id: message.id,
      role: message.role,
      text: message.body,
      ...(message.attachment ? { attachment: message.attachment } : {}),
      ...(message.isFinal !== undefined ? { isFinal: message.isFinal } : {}),
      ...(message.status ? { status: message.status } : {}),
    }))
  )
}

export function buildFallbackBriefingFromForm(
  form: ConciergeFormData,
  profile?: { name: string; email: string } | null
): IntelligenceBriefing {
  const name = form.name.trim() || profile?.name || 'Business Professional'
  const email = form.workEmail.trim() || profile?.email || ''
  const company =
    form.company?.trim() || email.split('@')[1]?.replace(/^www\./, '') || 'Your organization'
  const role = form.role?.trim() || 'Decision maker'

  return {
    person: {
      name,
      title: role,
      company,
      matchConfidence: preflightFailedConfidence(form, profile),
    },
    strategicContext: {
      domain: company,
      description:
        'Session context is ready. Public research may still be limited for this identity.',
      contextBullets: ['Intake-only context — no public signals found yet.'],
    },
    verification: {
      method: 'Intake confirmed',
      sources: ['Session intake'],
    },
    sources: [],
  }
}

function preflightFailedConfidence(
  form: ConciergeFormData,
  profile?: { name: string; email: string } | null
): number {
  if (form.name.trim() || profile?.name) return 62
  return 48
}

export function buildConciergeSearchRows(input: {
  briefing: IntelligenceBriefing | null
  researchStatus: string
  isResearching: boolean
}): ConciergeSourceRow[] {
  const rows: ConciergeSourceRow[] = []
  const statusHint = input.researchStatus.trim()

  if (statusHint) {
    rows.push({
      id: 'research-status',
      label: 'Public research',
      detail: statusHint,
      status: input.isResearching ? 'running' : 'checked',
    })
  }

  for (const source of input.briefing?.sources ?? []) {
    rows.push({
      id: source.id,
      label: source.label,
      detail: source.url ? hostLabel(source.url) : 'Verified source',
      status: source.status === 'pending' ? 'active' : 'checked',
    })
  }

  if (rows.length === 0 && input.isResearching) {
    rows.push(
      {
        id: 'identity',
        label: 'Identity check',
        detail: 'Matching name and email to public records',
        status: 'running',
      },
      {
        id: 'company',
        label: 'Company profile',
        detail: 'Reviewing company domain and public footprint',
        status: 'queued',
      },
      {
        id: 'sources',
        label: 'Source verification',
        detail: 'Collecting citations for the briefing',
        status: 'queued',
      }
    )
  }

  return rows.slice(0, 6)
}

export function buildBriefingFromResearchContext(
  research: ResearchResult | null | undefined,
  intelligenceContext?: Record<string, unknown> | null
): IntelligenceBriefing | null {
  if (!research) return null

  const stubItem: TranscriptItem = {
    id: 'briefing-from-research',
    role: 'model',
    text: '',
    timestamp: new Date(),
    isFinal: true,
    ...(research.groundingMetadata ? { groundingMetadata: research.groundingMetadata } : {}),
  }

  return mapBriefingData(
    {
      research,
      verification:
        (intelligenceContext?.verification as Record<string, unknown> | undefined) || {},
      trustedIdentity: intelligenceContext?.trustedIdentity,
    },
    stubItem
  )
}

export function extractFutureBriefingFromTranscript(
  items: TranscriptItem[]
): IntelligenceBriefing | null {
  for (let index = items.length - 1; index >= 0; index -= 1) {
    const item = items[index]
    if (item?.attachment?.type !== 'intelligence_briefing' || !item.attachment.data) continue

    try {
      const parsed = JSON.parse(item.attachment.data) as Record<string, any>
      return mapBriefingData(parsed, item)
    } catch {
      return null
    }
  }

  return null
}

function formatTimestamp(d: Date | number | string | undefined): string {
  if (!d) return ''
  const date = d instanceof Date ? d : new Date(d)
  return date.toLocaleTimeString(undefined, {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  })
}

function extractSourcesFromItem(item: TranscriptItem): FutureSource[] {
  const contextSources: FutureSource[] = (item.contextSources || [])
    .filter(source => source.type !== 'conversation')
    .map((source, index) => {
      const rawTitle = source.label || source.value || 'Source'
      const url =
        source.url || (/^https?:\/\//i.test(source.value || '') ? source.value : undefined)
      return normalizeSource({
        idSeed: url || source.value || `${source.type}-${source.label}-${index}`,
        label: source.label || source.type || (url ? hostLabel(url) : 'Source'),
        title: rawTitle,
        url,
      })
    })

  const groundingSources: FutureSource[] = (item.groundingMetadata?.groundingChunks || [])
    .map((chunk, index) => {
      const web = chunk.web || chunk.maps
      if (!web?.uri) return null
      return normalizeSource({
        idSeed: web.uri,
        label: hostLabel(web.uri),
        title: web.title || hostLabel(web.uri),
        url: web.uri,
        fallbackIndex: index,
      })
    })
    .filter(Boolean) as FutureSource[]

  return dedupeSources([...contextSources, ...groundingSources])
}

function mapBriefingData(
  parsed: Record<string, any>,
  item: TranscriptItem
): IntelligenceBriefing | null {
  const research = parsed.research || {}
  const verification = parsed.verification || {}
  const person = research.person || {}
  const company = research.company || {}
  const confidence = toPercent(verification.confidence ?? research.confidence ?? 0)
  const companyName =
    verification.company ||
    resolveVisitorFacingCompanyName({
      statedCompany: verification.company,
      personCompany: person.company,
      researchName: company.name,
      projectionName: company.name,
      emailDomain: company.domain,
      companyUrl: company.website,
    }) ||
    company.name ||
    person.company ||
    ''
  const rawRole = verification.role || person.role || research.role || ''
  const role = rawRole && !isGenericLeadRole(rawRole) ? rawRole : ''
  const sources = dedupeSources([
    ...sourcesFromCitations(research.citations),
    ...sourcesFromStrings(verification.sources),
    ...extractSourcesFromItem(item),
  ]).slice(0, 6)
  const description =
    company.summary ||
    company.description ||
    research.summary ||
    (role && companyName ? `${role} at ${companyName}` : '') ||
    'Public context is ready for this conversation.'

  if (!research.person && !research.company && sources.length === 0) return null

  // Build 2-3 plain-language context bullets from public research data (no internal fields)
  const contextBullets: string[] = []

  if (company.industry) {
    contextBullets.push(`Operates in ${company.industry}`)
  }

  const rawSummary = company.summary || company.description || ''
  if (rawSummary) {
    const trimmed =
      rawSummary.length > 140 ? rawSummary.slice(0, 140).trimEnd() + '...' : rawSummary
    contextBullets.push(trimmed)
  }

  const loc = person.location || company.country || ''
  if (loc && loc.length > 0 && loc.length < 60) {
    contextBullets.push(`Based in ${loc}`)
  }

  if (confidence >= 50 && sources.length > 0) {
    contextBullets.push(
      `${sources.length} public source${sources.length === 1 ? '' : 's'} backing this profile`
    )
  } else if (sources.length === 0 && confidence < 50) {
    contextBullets.push('Public signals are limited — confirm during conversation')
  }

  return {
    person: {
      name: person.fullName || person.name || verification.name || 'Business Professional',
      title: role,
      company: companyName,
      matchConfidence: confidence,
    },
    strategicContext: {
      domain: company.domain || company.website || companyName || 'Business context',
      description,
      ...(person.location || company.country
        ? { location: person.location || company.country }
        : {}),
      ...(company.industry ? { industry: company.industry } : {}),
      ...(rawSummary ? { companySummary: rawSummary } : {}),
      ...(contextBullets.length > 0 ? { contextBullets: contextBullets.slice(0, 3) } : {}),
    },
    verification: {
      method: verification.verified
        ? 'Verified identity'
        : parsed.trustedIdentity
          ? 'Intake confirmed'
          : 'Public research',
      sources: sources.map(source => source.title),
    },
    sources: sources.map(source => ({
      id: source.id,
      label: source.title,
      ...(source.url ? { url: source.url } : {}),
      status: 'verified' as const,
    })),
    ...(() => {
      const pack = readGroundedPack(parsed) || readGroundedPack(research)
      return pack ? { groundedPack: pack } : {}
    })(),
  }
}

function sourcesFromCitations(citations: unknown): FutureSource[] {
  if (!Array.isArray(citations)) return []

  return citations
    .map((citation, index) => {
      const item = citation as { uri?: string; url?: string; title?: string; description?: string }
      const url = item.uri || item.url
      if (!url) return null
      return normalizeSource({
        idSeed: url,
        label: hostLabel(url),
        title: item.title || hostLabel(url),
        url,
        fallbackIndex: index,
      })
    })
    .filter(Boolean) as FutureSource[]
}

function sourcesFromStrings(sources: unknown): FutureSource[] {
  if (!Array.isArray(sources)) return []

  return sources
    .map((source, index) => {
      if (typeof source !== 'string' || !source.trim()) return null
      const trimmed = source.trim()
      const looksLikeUrl = /^https?:\/\//i.test(trimmed)
      return normalizeSource({
        idSeed: trimmed,
        label: looksLikeUrl ? hostLabel(trimmed) : trimmed,
        title: looksLikeUrl ? hostLabel(trimmed) : trimmed,
        url: looksLikeUrl ? trimmed : undefined,
        fallbackIndex: index,
      })
    })
    .filter(Boolean) as FutureSource[]
}

export function formatSourceDisplayTitle(
  source: Pick<FutureSource, 'title' | 'label' | 'url'>
): string {
  const candidate = (source.title || source.label || '').trim()
  if (!candidate) return source.url ? hostLabel(source.url) : 'Source'
  if (/^https?:\/\//i.test(candidate)) return hostLabel(candidate)
  if (source.url && candidate === source.url) return hostLabel(source.url)
  return candidate
}

function normalizeSource(input: {
  idSeed: string
  label: string
  title: string
  url?: string | undefined
  fallbackIndex?: number | undefined
}): FutureSource {
  const key = input.idSeed || `${input.label}-${input.fallbackIndex ?? 0}`
  const url = input.url?.trim() || undefined
  const label = /^https?:\/\//i.test(input.label) && url ? hostLabel(url) : input.label
  const title = formatSourceDisplayTitle({ title: input.title, label, url })
  return {
    id: stableSourceId(key),
    label,
    title,
    ...(url ? { url } : {}),
  }
}

export function resolveMessageDisplaySources(
  messageSources: FutureSource[] | undefined,
  sessionSources: FutureSource[] | undefined,
  isLastAssistant: boolean
): FutureSource[] {
  const own = messageSources ?? []
  if (own.length > 0) return own
  if (!isLastAssistant) return []
  return sessionSources ?? []
}

export function resolveDetailsConfidenceLabel(
  sourceCount: number,
  sessionConfidence: number
): string {
  if (sourceCount === 0) return '—'
  return `${sessionConfidence}%`
}

/** Omit Mode until thinking; never paint `idle` on a finished greeting. */
export function resolveDetailsModeLabel(input: {
  isThinking: boolean
  agentMode?: string | null | undefined
  sessionModeLabel: string
}): string | undefined {
  if (input.isThinking) return input.agentMode || 'thinking'
  const label = input.sessionModeLabel?.trim()
  if (!label || /^idle$/i.test(label)) return undefined
  return label
}

export function buildWhatIFoundSummary(input: {
  name?: string | null
  company?: string | null
  role?: string | null
  hook?: string | null
}): string | null {
  const name = input.name?.trim() || null
  const company = input.company?.trim() || null
  const role = input.role?.trim() || null
  const hook = input.hook?.trim() || null
  const sentences: string[] = []

  if (name && company && role) sentences.push(`${name} is ${role} at ${company}.`)
  else if (name && company) sentences.push(`${name} at ${company}.`)
  else if (company && role) sentences.push(`${role} at ${company}.`)
  else if (company) sentences.push(company)
  else if (name && role) sentences.push(`${name} — ${role}.`)

  if (hook) {
    const clipped = hook.length > 220 ? `${hook.slice(0, 217)}…` : hook
    if (!sentences.some(sentence => sentence.includes(clipped))) sentences.push(clipped)
  }

  return sentences.length > 0 ? sentences.join(' ') : null
}

function dedupeSources(sources: FutureSource[]): FutureSource[] {
  const seen = new Map<string, FutureSource>()
  for (const source of sources) {
    const key = source.url || source.id
    if (!seen.has(key)) seen.set(key, source)
  }
  return Array.from(seen.values())
}

function stableSourceId(value: string): string {
  const normalized = value.toLowerCase().trim()
  let hash = 0
  for (let i = 0; i < normalized.length; i += 1) {
    hash = (hash * 31 + normalized.charCodeAt(i)) >>> 0
  }
  return `src-${hash.toString(36)}`
}

function hostLabel(url: string): string {
  try {
    return new URL(url).hostname.replace(/^www\./, '')
  } catch {
    return url.replace(/^https?:\/\//i, '').split('/')[0] || 'Source'
  }
}

function toPercent(value: number): number {
  if (!Number.isFinite(value)) return 0
  if (value <= 1) return Math.round(value * 100)
  return Math.round(value)
}

function formatStageLabel(stage: string): string {
  const normalized = stage.toUpperCase()
  if (['DISCOVERY', 'INTELLIGENCE_GATHERING'].includes(normalized)) return 'Understanding'
  if (['QUALIFIED', 'SCORING', 'OBJECTION'].includes(normalized)) return 'Exploring'
  if (['WORKSHOP_PITCH', 'CONSULTING_PITCH', 'PITCHING', 'PROPOSAL'].includes(normalized)) {
    return 'Recommendations'
  }
  if (['CLOSING', 'BOOKING_REQUESTED', 'BOOKED', 'SUMMARY'].includes(normalized)) return 'Report'
  return stage
    .toLowerCase()
    .replace(/_/g, ' ')
    .replace(/\b\w/g, char => char.toUpperCase())
}

const BOOKING_FOOTER_STAGES: ReadonlySet<FunnelStage> = new Set([
  'WORKSHOP_PITCH',
  'CONSULTING_PITCH',
  'PITCHING',
  'PROPOSAL',
  'OBJECTION',
  'CLOSING',
  'BOOKING_REQUESTED',
  'BOOKED',
  'SUMMARY',
])

export function shouldShowFutureBookingFooter(stage: FunnelStage | undefined): boolean {
  if (!stage) return false
  return BOOKING_FOOTER_STAGES.has(stage)
}

// ── SessionEvidenceState builder (#123) ──

import type { SessionEvidenceState, EvidenceMode, ConfidenceTier } from './future-chat-types'

interface BuildSessionEvidenceStateInput {
  briefing: IntelligenceBriefing | null
  formData?: ConciergeFormData
  phase?: string
  transcript?: TranscriptItem[]
  evidence?: {
    visualObservations?: Array<{
      modality: 'webcam' | 'screen'
      analysis: string
      at?: string
      verified?: boolean
    }>
    voiceObservations?: Array<{ transcript: string; at?: string }>
    capabilitiesUsed?: string[]
  }
  researchRan?: boolean
  researchFailed?: boolean
  voiceMinutes?: number
  screenMinutes?: number
  filesUploaded?: number
  hasReportBeenGenerated?: boolean
  hasBookingBeenRequested?: boolean
}

export function buildSessionEvidenceState(
  input: BuildSessionEvidenceStateInput
): SessionEvidenceState {
  const {
    briefing,
    formData,
    phase,
    transcript,
    evidence,
    researchRan = false,
    researchFailed = false,
    voiceMinutes = 0,
    screenMinutes = 0,
    filesUploaded = 0,
    hasReportBeenGenerated = false,
    hasBookingBeenRequested = false,
  } = input

  // ── Mode ──
  let mode: EvidenceMode
  if (phase === 'direct') {
    mode = 'direct_session'
  } else if (researchFailed) {
    mode = 'research_failed'
  } else if (briefing && briefing.sources.length > 0) {
    mode = 'public_research'
  } else if (researchRan && (!briefing || briefing.sources.length === 0)) {
    mode = 'intake_only'
  } else {
    mode = 'intake_only'
  }

  // ── Source count ──
  const sourceCount = briefing?.sources?.length ?? 0
  const sources = (briefing?.sources ?? []).map(s => ({
    id: s.id,
    label: s.label,
    ...(s.url ? { url: s.url } : {}),
    status: s.status as 'verified' | 'pending',
  }))

  // ── Confidence tier ──
  let confidenceTier: ConfidenceTier
  let confidenceScore: number | undefined

  if (sourceCount === 0) {
    confidenceTier = 'not_source_backed'
    confidenceScore = undefined
  } else {
    const score = briefing?.person.matchConfidence ?? 0
    confidenceScore = score
    if (sourceCount >= 6 && score >= 70) {
      confidenceTier = 'high'
    } else if (sourceCount >= 3 && score >= 50) {
      confidenceTier = 'moderate'
    } else {
      confidenceTier = 'low'
    }
  }

  // ── Identity ──
  const companyName = briefing?.person.company || formData?.company?.trim() || undefined
  const personName = briefing?.person.name || formData?.name?.trim() || undefined
  const role = briefing?.person.title || formData?.role?.trim() || undefined
  const domain = briefing?.strategicContext?.domain || companyName || undefined
  const location = briefing?.strategicContext?.location || undefined
  const identityConfirmed = phase === 'confirmed' || phase === 'complete'
  const sourceBackedIdentity =
    sourceCount > 0 && briefing?.verification?.method === 'Verified identity'

  // ── Research metadata ──
  const industry = briefing?.strategicContext?.industry
  const companySummary = briefing?.strategicContext?.companySummary
  const contextBullets = briefing?.strategicContext?.contextBullets ?? []

  // ── Multimodal evidence ──
  const visualObservations = (evidence?.visualObservations ?? [])
    .filter(obs => obs.analysis?.trim())
    .map(obs => ({
      modality: obs.modality,
      analysis: obs.analysis,
      ...(obs.at ? { timestamp: obs.at } : {}),
    }))

  const webcamObservations = visualObservations.filter(o => o.modality === 'webcam').length
  const screenShareObservations = Math.max(
    visualObservations.filter(o => o.modality === 'screen').length,
    screenMinutes > 0 ? 1 : 0
  )

  const voiceObsCount = evidence?.voiceObservations?.length ?? 0
  const resolvedVoiceMinutes =
    voiceMinutes > 0 ? voiceMinutes : voiceObsCount > 0 ? Math.max(1, voiceObsCount) : 0

  const resolvedFilesAnalyzed =
    filesUploaded > 0
      ? filesUploaded
      : evidence?.capabilitiesUsed?.includes('file_analysis')
        ? 1
        : 0

  // ── Turn count ──
  const turnCount = transcript?.filter(t => t.role === 'user' && t.isFinal).length ?? 0

  return {
    mode,
    identityConfirmed,
    sourceBackedIdentity,
    ...(companyName ? { companyName } : {}),
    ...(personName ? { personName } : {}),
    ...(role ? { role } : {}),
    ...(domain ? { domain } : {}),
    ...(location ? { location } : {}),
    sourceCount,
    sources,
    confidenceTier,
    ...(confidenceScore !== undefined ? { confidenceScore } : {}),
    ...(industry ? { industry } : {}),
    ...(companySummary ? { companySummary } : {}),
    contextBullets,
    voiceMinutes: resolvedVoiceMinutes,
    webcamObservations,
    screenShareObservations,
    filesAnalyzed: resolvedFilesAnalyzed,
    visualObservations,
    turnCount,
    hasReportBeenGenerated,
    hasBookingBeenRequested,
  }
}
